const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MAX_DAYS_OUT = 5;

export const isSchedulableDate = (selectedDate) => {
  if (!selectedDate) return false;

  const dateObj = new Date(`${selectedDate}T00:00:00`);
  const dayOfWeek = dateObj.getDay(); // 0 = Sunday

  // Today at midnight
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffDays = Math.floor((dateObj.getTime() - today.getTime()) / MS_PER_DAY);

  // Only allow: today..today+5 (calendar days), and never Sunday
  if (diffDays < 0 || diffDays > MAX_DAYS_OUT) return false;
  if (dayOfWeek === 0) return false;

  return true;
};

// Status semantics (based on your app):
// 0 = Scheduled, 1 = Show, 2 = No-show, 3 = Cancelled (NOT cleared), 4 = Rescheduled, 5 = Cleared/Deleted (slot freed)
// Rule: a cancelled appointment (3) STILL blocks the time slot until staff clears it (5).
const blocksSlot = (status) => status !== 5;

export const getAvailableTimes = (
  appointments,
  closures,
  noLanguages,
  selectedDate,
  selectedLocation,
  locations,
  client
) => {
  if (!selectedLocation || !selectedDate) return [];
  if (!isSchedulableDate(selectedDate)) return [];

  const dateObj = new Date(selectedDate + "T00:00:00");
  const dayOfWeek = dateObj.getDay(); // 0=Sun..6=Sat

  const formattedDate = `${(dateObj.getMonth() + 1).toString().padStart(2, "0")}/${dateObj
    .getDate()
    .toString()
    .padStart(2, "0")}/${dateObj.getFullYear()}`;

  const locationIdx = parseInt(selectedLocation, 10);

  const location = locations.find((loc) => Number(loc.id) === locationIdx);
  if (!location) return [];

  // Block closed weekdays (covers Sunday because daysOpen[0] should be false)
  if (!location.daysOpen?.[dayOfWeek]) return [];

  return [...Array(24).keys()].filter((hour) => {
    const isWithinOperatingHours = hour >= location.openTime && hour < location.closeTime;

    // IMPORTANT: cancelled-but-not-cleared (status 3) still blocks the slot.
    // Only status 5 should free the slot.
    const hasAppointment = appointments.some((app) => {
      return (
        app.date === formattedDate &&
        Number(app.startTime) === hour &&
        Number(app.location) === locationIdx &&
        blocksSlot(app.status)
      );
    });

    const hasClosure = closures.some(
      (closure) =>
        closure.date === formattedDate &&
        closure.startTime <= hour &&
        closure.endTime > hour &&
        Number(closure.location) === locationIdx
    );

    const clientLanguageUnavailable = noLanguages.some(
      (u) =>
        u.date === formattedDate &&
        Number(u.location) === locationIdx &&
        u.language === client?.language
    );

    return isWithinOperatingHours && !hasAppointment && !hasClosure && !clientLanguageUnavailable;
  });
};

export const getFilteredLocations = (selectedDate, locations) => {
  if (!selectedDate) return [];
  if (!isSchedulableDate(selectedDate)) return [];

  const dateObj = new Date(selectedDate + "T00:00:00");
  const dayOfWeek = dateObj.getDay();

  return locations.filter((location) => location.daysOpen?.[dayOfWeek]);
};
